import {
  Alert,
  Button,
  Grid,
  Typography,
  Box,
  Fade,
  Paper,
} from "@mui/material";
import { useState } from "react";
import { useBlockedDates } from "../../../../hooks/useBlockedDates";
import { AppDatePicker } from "../../../../components/datepickers";
import { useDispatch } from "react-redux";
import {
  convertToTimezone,
  DATE_FORMATS,
  reactDatePickerFilterPassedTime,
} from "../../../../services/date-and-time.utils";
import { submitManualMeeting } from "../../../../apis/clients.api";
import { AppModalDialogBox } from "../../../../components/modals";
import {
  CalendarToday,
  Schedule,
  AccessTime,
  CheckCircle,
} from "@mui/icons-material";
import { SlotDetailsCard } from "../../../../components/cards/slot-details-card";
import { SaveAndCancelButton } from "../../../../components/buttons";

export default function ScheduleManualInvitationModal({
  invitation,
  onSubmit,
  onClose,
}) {
  const [selectedSlotTimeFrom, setSelectedSlotTimeFrom]: any = useState();
  const dispatch = useDispatch();
  const [notify, setNotify] = useState("");
  const { blockedDates, onMonthChange } = useBlockedDates();

  function convertToAmbassadorTimezone(date, timezone) {
    const to = new Date(date?.getTime() + 45 * 60000);
    return {
      selectedSlotTimeFrom: date,
      selectedSlotTimeTo: to,
      convertedSlotTimeFrom: convertToTimezone(
        date,
        timezone,
        DATE_FORMATS.fullDateWithTime
      ),
      convertedSlotTimeTo: convertToTimezone(
        to,
        timezone,
        DATE_FORMATS.fullDateWithTime
      ),
    };
  }

  function convertToAmbassadorAndClientTimezone(date) {
    const to = new Date(date?.getTime() + 45 * 60000);
    return {
      clientTimezoneConvertedDateTimeFrom: convertToTimezone(
        date,
        invitation?.client?.timezone?.value,
        DATE_FORMATS.fullDateWithTime
      ),
      clientTimezoneConvertedDateTimeTo: convertToTimezone(
        to,
        invitation?.client?.timezone?.value,
        DATE_FORMATS.fullDateWithTime
      ),
      ambassadorTimezoneConvertedDateTimeFrom: convertToTimezone(
        date,
        invitation?.ambassador?.timezone?.value,
        DATE_FORMATS.fullDateWithTime
      ),
      ambassadorTimezoneConvertedDateTimeTo: convertToTimezone(
        to,
        invitation?.ambassador?.timezone?.value,
        DATE_FORMATS.fullDateWithTime
      ),
    };
  }

  const handleDateChange = ({ date, timezone }) => {
    const selectedDatetime: any = convertToAmbassadorTimezone(date, timezone);

    setSelectedSlotTimeFrom(selectedDatetime);
  };

  const handleScheduleClick = async () => {
    const payload = {
      ambassadorInvitationId: invitation?.ambassadorInvitationId,
      timeSlot: {
        from: selectedSlotTimeFrom?.selectedSlotTimeFrom,
        to: selectedSlotTimeFrom?.selectedSlotTimeTo,
      },
    };

    const response: any = await dispatch(submitManualMeeting(payload));

    if (response?.success) {
      onSubmit();
    }
  };

  return (
    <AppModalDialogBox
      isModalOpen={true}
      title="Schedule Meeting"
      handleClose={onClose}
      size={"sm"}
      actionButtonRenderer={
        <SaveAndCancelButton
          primaryText={"Schedule"}
          defaultText={"Cancel"}
          onPrimaryActionClick={handleScheduleClick}
          onDefaultButtonClick={onClose}
          isPrimaryButtonDisabled={!selectedSlotTimeFrom}
        />
      }
    >
      <Box sx={{ p: 1 }}>
        {/* Info Alert */}
        <Alert
          severity="info"
          sx={{
            mb: 3,
            borderRadius: 2,
            "& .MuiAlert-message": {
              fontSize: 14,
              fontWeight: 500,
            },
          }}
          icon={<Schedule />}
        >
          Choose a new date and time for the meeting. The meeting will remain
          under the same invitation.
        </Alert>

        {/* Date Selection Section */}
        <Paper
          elevation={0}
          sx={{
            p: 3,
            mb: 3,
            borderRadius: 3,
            border: "1px solid #e0e0e0",
            backgroundColor: "#fafafa",
          }}
        >
          <Box sx={{ display: "flex", alignItems: "center", mb: 2 }}>
            <CalendarToday sx={{ mr: 1.5, color: "primary.main" }} />
            <Typography
              variant="h6"
              sx={{
                fontWeight: 600,
                color: "text.primary",
                fontSize: 16,
              }}
            >
              Select Date & Time
            </Typography>
          </Box>

          <AppDatePicker
            dateFormat={"Pp"}
            showTimeSelect
            selectedDate={selectedSlotTimeFrom?.selectedSlotTimeFrom}
            setDate={(date) =>
              handleDateChange({
                date,
                timezone: invitation?.ambassador?.timezone?.value,
              })
            }
            minDate={new Date()}
            filterTime={reactDatePickerFilterPassedTime}
            blockedDates={blockedDates}
            onMonthChange={onMonthChange}
          />
        </Paper>

        {/* Timezone Details */}
        {selectedSlotTimeFrom && (
          <Box>
            <Typography
              variant="h6"
              sx={{
                mb: 2,
                fontWeight: 600,
                color: "text.primary",
                display: "flex",
                alignItems: "center",
                fontSize: 16,
              }}
            >
              <AccessTime sx={{ mr: 1.5, color: "primary.main" }} />
              Meeting Times
            </Typography>

            <Grid container spacing={2}>
              <Grid item xs={12} sm={6}>
                <SlotDetailsCard
                  title={`${invitation?.ambassador?.firstName} (Ambassador)`}
                  from={
                    convertToAmbassadorAndClientTimezone(
                      selectedSlotTimeFrom?.selectedSlotTimeFrom
                    ).ambassadorTimezoneConvertedDateTimeFrom
                  }
                  to={
                    convertToAmbassadorAndClientTimezone(
                      selectedSlotTimeFrom?.selectedSlotTimeFrom
                    ).ambassadorTimezoneConvertedDateTimeTo
                  }
                />
              </Grid>
              <Grid item xs={12} sm={6}>
                <SlotDetailsCard
                  title={`${invitation?.client?.firstName} (Vendor)`}
                  from={
                    convertToAmbassadorAndClientTimezone(
                      selectedSlotTimeFrom?.selectedSlotTimeFrom
                    ).clientTimezoneConvertedDateTimeFrom
                  }
                  to={
                    convertToAmbassadorAndClientTimezone(
                      selectedSlotTimeFrom?.selectedSlotTimeFrom
                    ).clientTimezoneConvertedDateTimeTo
                  }
                />
              </Grid>
            </Grid>

            {notify && (
              <Typography
                sx={{
                  color: "error.main",
                  mt: 2,
                  fontSize: 13,
                  fontWeight: 500,
                }}
              >
                {notify}
              </Typography>
            )}
          </Box>
        )}
      </Box>
    </AppModalDialogBox>
  );
}
